import { ReactNode, useEffect, useMemo, useState } from "react";
import IdleTimerContainer from "./IdleTimerContainer";
import SubscriptionChange from "./subscriptions/subscriptionChange";
import styles from "./styles";
import "./styles.css";
import { Drawer, Layout, Menu, Spin } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import sicom_logo from "../../../../assets/images/mini_sicom_logo.png";
import s_logo from "../../../../assets/images/mini_s.png";
import { getApplicationVersion } from "api/generated";
import { useClient } from "hooks/useClient";
import { RouteComponentProps, withRouter } from "react-router-dom";
import HeaderBar from "./HeaderBar";
import { LoadingOutlined } from "@ant-design/icons";
import { getTokenData } from "security";
import { useDistributionPoints } from "hooks/distributionPoints/useDistributionPoints";
import { MenuItem } from "../menu/menuTypes";
import { buildMenuItems } from "./menu/menuUtils";
import { useIsMobile } from "hooks/useIsMobile";
import { ItemType } from "antd/es/menu/interface";
import DistributionPointSelector from "common/components/widgets/DistributionPoint/DistributionPointSelector";

interface MainLayoutProps extends RouteComponentProps {
  children: ReactNode;
  menu: MenuItem[];
}

function MainLayout({ children, menu, location }: MainLayoutProps) {
  const { client } = useClient();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [appVersion, setAppVersion] = useState("desarrollo");
  const {
    isLoading,
    userDistributionPoints,
    onSelectDistributionPointId,
    distributionPoint,
  } = useDistributionPoints();
  const { isMobile } = useIsMobile();
  const [userData, setUserData] = useState({
    userEmail: "",
    userRole: "",
    inventoryControl: false,
  });

  useEffect(() => {
    getApplicationVersion({ client }).then(({ data }) => {
      setAppVersion(data ?? "desarrollo");
    });

    const data = getTokenData() as {
      userEmail: string;
      userRole: string;
      permissions: { inventoryControl: { edit: boolean } };
    };
    setUserData({
      userEmail: data.userEmail,
      userRole: data.userRole,
      inventoryControl: data.permissions.inventoryControl.edit,
    });
  }, []);

  function openSubMenu() {
    if (
      location.pathname === "/dispensaries" ||
      location.pathname === "/dispensarySale" ||
      location.pathname === "/hoseSale"
    ) {
      return ["/dispensaries"];
    } else if (
      location.pathname === "/entryVolumeMeasurer" ||
      location.pathname === "/receptions"
    ) {
      return ["/entryVolumeMeasurer"];
    }
    return [location.pathname];
  }

  // Para desktop
  const menuItemsDesktop = useMemo(() => {
    const distributionPointSelector: ItemType | undefined =
      userDistributionPoints.length > 0
        ? {
            key: "distribution-point-selector",
            label: (
              <div style={{ paddingBottom: "36px" }}>
                <DistributionPointSelector
                  allowedDistributionPoints={userDistributionPoints}
                  selectedDistributionPoint={distributionPoint?.id || ""}
                  onChangeDistributionPoint={onSelectDistributionPointId}
                />
              </div>
            ),
            type: "group",
          }
        : undefined;

    return buildMenuItems(menu, collapsed, distributionPointSelector);
  }, [menu, collapsed, userDistributionPoints, distributionPoint]);

  // Para mobile
  const menuItemsMobile = useMemo(() => {
    const distributionPointSelector: ItemType | undefined =
      userDistributionPoints.length > 0
        ? {
            key: "distribution-point-selector",
            label: (
              <div style={{ paddingBottom: "36px" }}>
                <DistributionPointSelector
                  allowedDistributionPoints={userDistributionPoints}
                  selectedDistributionPoint={distributionPoint?.id || ""}
                  onChangeDistributionPoint={onSelectDistributionPointId}
                />
              </div>
            ),
            type: "group",
          }
        : undefined;

    return buildMenuItems(menu, false, distributionPointSelector);
  }, [menu, userDistributionPoints, distributionPoint]);

  return (
    <div>
      <IdleTimerContainer />
      <SubscriptionChange />

      <Layout style={styles.layout}>
        {isMobile ? (
          <>
            {/* Botón flotante para mobile */}
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Abrir menú"
              style={{
                position: "fixed",
                top: 16,
                left: 16,
                zIndex: 1000,
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: "#24a0d6",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
              }}
            >
              <MenuOutlined style={{ color: "#fff", fontSize: 18 }} />
            </button>

            {/* Drawer [ara mobile] */}
            <Drawer
              placement="left"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              width={315}
              closeIcon={null}
              title={null}
              styles={{
                body: {
                  padding: 0,
                  background: "#F1F1F1",
                  overflow: "hidden",
                },
                header: { display: "none" },
              }}
            >
              <div className="logo">
                <img src={sicom_logo} alt="Sicom" />
              </div>

              {/* mode="inline" para que los submenús se expandan hacia abajo */}
              <Menu
                theme="dark"
                items={menuItemsMobile}
                defaultSelectedKeys={[location.pathname]}
                defaultOpenKeys={openSubMenu()}
                mode="inline"
                style={{
                  height: `calc(100vh - 250px)`,
                  overflowY: "auto",
                }}
              />

              <div className="appVersion" />
              <div className="appVersion">
                <p>
                  <b>Control Volumétrico v-{appVersion}</b>
                </p>
              </div>
              <div style={{ height: "48px" }} />
            </Drawer>
          </>
        ) : (
          /* Sider desktop */
          <Layout.Sider
            breakpoint="xl"
            width="315"
            style={{
              overflow: "hidden",
              height: "100vh",
              position: "sticky",
              top: 0,
              left: 0,
            }}
            collapsible
            collapsed={collapsed}
            onCollapse={() => setCollapsed(!collapsed)}
          >
            <div className="logo">
              <img src={!collapsed ? sicom_logo : s_logo} alt="Sicom" />
            </div>

            <Menu
              theme="dark"
              inlineCollapsed={collapsed}
              items={menuItemsDesktop}
              defaultSelectedKeys={[location.pathname]}
              defaultOpenKeys={openSubMenu()}
              mode="vertical"
              style={{
                height: `calc(100vh - 250px)`,
                overflowY: "auto",
              }}
            />

            <div className="appVersion" />
            <div className="appVersion">
              <p>
                <b>
                  {collapsed ? "v-" : "Control Volumétrico v-"}
                  {appVersion}
                </b>
              </p>
            </div>
            <div style={{ height: "48px" }} />
          </Layout.Sider>
        )}

        {/* Contenido principal */}
        <Layout>
          <Layout.Header
            style={{
              ...styles.header,
              // En mobile: margen izquierdo para que el título no quede
              // tapado por el botón flotante del menú (44px botón)
              ...(isMobile ? { paddingLeft: 44 } : {}),
            }}
          >
            <HeaderBar
              pathname={location.pathname}
              userRole={userData.userRole}
              userEmail={userData.userEmail}
              menu={menu}
            />
          </Layout.Header>
          <Layout.Content
            style={{
              ...styles.content,
              // Padding para que el contenido no quede pegado a los bordes
              ...(isMobile ? { margin: 0, padding: "16px 12px" } : {}),
            }}
          >
            {isLoading && (
              <div className="justify-center">
                <Spin
                  indicator={<LoadingOutlined style={styles.spin} spin />}
                />
              </div>
            )}
            {children}
          </Layout.Content>
        </Layout>
      </Layout>
    </div>
  );
}

export default withRouter(MainLayout);
